import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';

import { useGetHolidays } from '@repo/api/holiday/useGetHolidays';
import { useGetAssignedMeetings } from '@repo/api/meeting/useGetAssignedMeetings';
import { useGetParentCalendar } from '@repo/api/parent/useGetParentCalendar';
import { isParent } from '@repo/lib/user-lvl/isParent';
import { useCallback, useMemo } from 'react';

import { useCredentials } from '@/store/auth-store';
import { CalendarEvent, mergeCalendarEvents } from '@/utils/calendar';

export function useCalendarEvents(rangeDates: EventCalendarRangeDates) {
  const { accountType } = useCredentials();
  const viewerIsParent = isParent(accountType);

  // A parent has no meetings of their own — theirs is the merged calendar of
  // every linked child. Branching here rather than in each view keeps all four
  // of them, and the header, working for both audiences unchanged.
  const assignedQuery = useGetAssignedMeetings(rangeDates, !viewerIsParent);
  const parentQuery = useGetParentCalendar(rangeDates, viewerIsParent);
  const meetingsQuery = viewerIsParent ? parentQuery : assignedQuery;

  const holidaysQuery = useGetHolidays(rangeDates);

  const events = useMemo<Array<CalendarEvent>>(() => {
    return mergeCalendarEvents(meetingsQuery.data ?? [], holidaysQuery.data ?? []);
  }, [holidaysQuery.data, meetingsQuery.data]);

  const refetch = useCallback(async () => {
    await Promise.all([meetingsQuery.refetch(), holidaysQuery.refetch()]);
  }, [holidaysQuery.refetch, meetingsQuery.refetch]);

  return {
    events,
    isFetching: meetingsQuery.isFetching || holidaysQuery.isFetching,
    isError: meetingsQuery.isError || holidaysQuery.isError,
    error: meetingsQuery.error ?? holidaysQuery.error,
    refetch,
  };
}
